import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { freeTrialApi, type TrialSetCode } from '@/api/freeTrials'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'
import { toast } from 'sonner'

export interface TrialSetChangeLearner {
  trialId: number
  userId: number
  name: string
  currentSet: TrialSetCode
}

interface Props {
  learner: TrialSetChangeLearner
  onClose: () => void
}

export function TrialSetChangeModal({ learner, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [targetSet, setTargetSet] = useState<TrialSetCode | undefined>()
  const [reason, setReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const catalogQuery = useQuery({
    queryKey: ['lcFreeTrialCatalog'],
    queryFn: async () => (await freeTrialApi.catalog()).data.data ?? [],
  })
  const setItems = (catalogQuery.data ?? [])
    .filter((item) => item.trialSetCode !== learner.currentSet && item.lessonCount === 5)
    .map((item) => ({
      value: item.trialSetCode,
      label: t('admin:roster.trial.changeSet.option', {
        set: item.trialSetCode,
        name: item.productName,
      }),
    }))

  const mutation = useMutation({
    mutationFn: (body: { trialSetCode: TrialSetCode; reason: string }) =>
      freeTrialApi.changeSet(learner.trialId, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lcRoster'] }),
        queryClient.invalidateQueries({ queryKey: ['lcRosterSearch'] }),
        queryClient.invalidateQueries({ queryKey: ['lcEnrollments'] }),
        queryClient.invalidateQueries({ queryKey: ['lcFreeTrials'] }),
        queryClient.invalidateQueries({ queryKey: ['lcFreeTrialHistory', learner.userId] }),
        queryClient.invalidateQueries({ queryKey: ['coaching', 'classes'] }),
      ])
      toast.success(t('admin:roster.trial.changeSet.success'))
      onClose()
    },
    onError: (error: unknown) => {
      const code = (error as { response?: { data?: { error?: { code?: string; message?: string } } } })
        ?.response?.data?.error?.code
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      toast.error(code
        ? t(`apiError.${code}`, { ns: 'common', defaultValue: message ?? t('admin:roster.trial.changeSet.failed') })
        : t('admin:roster.trial.changeSet.failed'))
    },
  })

  const submit = () => {
    const trimmedReason = reason.trim()
    if (!targetSet) {
      setValidationError(t('admin:roster.trial.changeSet.setRequired'))
      return
    }
    if (!trimmedReason || trimmedReason.length > 1000) {
      setValidationError(t('admin:roster.trial.changeSet.reasonRequired'))
      return
    }
    setValidationError(null)
    mutation.mutate({ trialSetCode: targetSet, reason: trimmedReason })
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !mutation.isPending) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin:roster.trial.changeSet.title', { name: learner.name })}</DialogTitle>
          <DialogDescription>
            {t('admin:roster.trial.changeSet.description', { set: learner.currentSet })}
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertTitle>{t('admin:roster.trial.changeSet.noticeTitle')}</AlertTitle>
          <AlertDescription>{t('admin:roster.trial.changeSet.notice')}</AlertDescription>
        </Alert>

        <FieldGroup>
          <Field data-invalid={validationError != null && !targetSet}>
            <FieldLabel>{t('admin:roster.trial.changeSet.setLabel')}</FieldLabel>
            <Select
              items={setItems}
              value={targetSet ?? null}
              onValueChange={(value) => {
                setTargetSet((value || undefined) as TrialSetCode | undefined)
                setValidationError(null)
              }}
              disabled={catalogQuery.isLoading || mutation.isPending}
            >
              <SelectTrigger className="w-full" aria-invalid={validationError != null && !targetSet}>
                <SelectValue placeholder={t('admin:roster.trial.changeSet.setPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {setItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {validationError != null && !targetSet ? <FieldError>{validationError}</FieldError> : null}
          </Field>

          <Field data-invalid={validationError != null && !reason.trim()}>
            <FieldLabel htmlFor="trial-set-change-reason">
              {t('admin:roster.trial.changeSet.reasonLabel')}
            </FieldLabel>
            <Textarea
              id="trial-set-change-reason"
              value={reason}
              maxLength={1000}
              aria-invalid={validationError != null && !reason.trim()}
              placeholder={t('admin:roster.trial.changeSet.reasonPlaceholder')}
              disabled={mutation.isPending}
              onChange={(event) => {
                setReason(event.target.value)
                setValidationError(null)
              }}
            />
            {validationError != null && !reason.trim() ? <FieldError>{validationError}</FieldError> : null}
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={mutation.isPending} onClick={onClose}>
            {t('common:button.cancel')}
          </Button>
          <Button type="button" disabled={mutation.isPending || catalogQuery.isLoading} onClick={submit}>
            {mutation.isPending ? <Spinner data-icon="inline-start" /> : null}
            {t('admin:roster.trial.changeSet.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
